import axios, { type AxiosInstance } from 'axios';
import type {
  AssistantUsageSummary,
  AuthUser,
  BugReportDto,
  BuiltInUser,
  CiClassDto,
  CorrelationRuleDto,
  CreateFeedbackRequest,
  CreateSavedNavLinkRequest,
  CreateSystemSlaPolicyTemplateDto,
  EmailLogDto,
  FeedbackDto,
  IncidentAiChatPlatformPolicy,
  IncidentAiChatPlatformTenantPolicy,
  LoginRequest,
  LoginResponse,
  PagedResult,
  PerformanceSummaryDto,
  PipelineDefaultsDto,
  PipelineDefaultsListDto,
  PlatformTeamsAppSettingsDto,
  RegisterRequest,
  RegisterResponse,
  ScheduledJobDto,
  SavedNavLinkDto,
  ScriptSummaryDto,
  ServiceConnectionListDto,
  SetPlatformTeamsAppSecretRequest,
  SignInLogsDetailedResponse,
  SignInLogsResponse,
  SystemInfoResponse,
  SystemLogsResponse,
  SystemPriorityTemplateDto,
  SystemReasonTemplateDto,
  SystemSettingsTyped,
  SystemSlaPolicyTemplateDto,
  Team,
  TenantPriorityConfigDto,
  TenantReasonConfigDto,
  TenantSummary,
  TeamsSetupInfoDto,
  TestPlatformTeamsAppCredentialsResult,
  AiBudgetStatusDto,
  SystemAiCreditsOverview,
  TenantAiCreditSummary,
  UpdateTenantAiCreditSettingsRequest,
  UpdateSystemSlaPolicyTemplateDto,
  UpsertPlatformTeamsAppRequest,
  UpsertSystemPriorityTemplateRequest,
  UpsertSystemReasonTemplateRequest,
  VersionHistoryResponse,
  PipelineDefaultsSummaryDto,
} from '../types';
import { API_BASE_URL } from '../config/apiConfig';
import { getCurrentCorrelationId } from './correlationId';
import type {
  RestoreTenantDeletionCommand,
  RestoreTenantResult,
  ScheduleTenantDeletionCommand,
  ScheduleTenantDeletionResult,
  TenantDeletionPreviewResult,
  TenantLifecycleListItem,
  TenantLifecycleStatusResult,
  TenantSubscriptionResult,
  UpdateTenantSubscriptionCommand,
  SetTenantDeletionProtectionCommand,
  SetTenantPurgeApprovalCommand,
  TenantDeletionProtectionResult,
  TenantPurgeApprovalResult,
} from '../types/tenantLifecycle';

type Query = Record<string, string | number | boolean | undefined>;
type AssistantUsageApiRow = {
  tenantId: string;
  tenantName?: string | null;
  totalRequests: number;
  totalPromptTokens: number;
  totalCompletionTokens: number;
  totalTokens: number;
};
type TenantSummaryApiResponse = Omit<TenantSummary, 'isActive'> & {
  isActive?: boolean | null;
  status?: string | null;
};

const ASSISTANT_INPUT_COST_PER_MILLION = 2.5;
const ASSISTANT_OUTPUT_COST_PER_MILLION = 10;

function toQueryString(query: Query): string {
  const params = new URLSearchParams();
  Object.entries(query).forEach(([key, value]) => {
    if (value !== undefined) {
      params.set(key, String(value));
    }
  });
  return params.toString();
}

function mapTenantSummary(tenant: TenantSummaryApiResponse): TenantSummary {
  const normalizedStatus = tenant.status?.toLowerCase();
  const isInactiveByStatus = normalizedStatus === 'inactive';
  const isActive = tenant.isActive !== false && !isInactiveByStatus;

  return {
    id: tenant.id,
    name: tenant.name,
    slug: tenant.slug,
    tier: tenant.tier,
    userCount: tenant.userCount,
    createdAt: tenant.createdAt,
    isActive,
  };
}

export interface ServiceBusHealthSummary {
  totalQueues: number;
  totalActiveMessages: number;
  totalDeadLetterMessages: number;
  overallStatus?: string;
}

export interface ServiceBusQueueMetrics {
  queueName: string;
  activeMessageCount: number;
  deadLetterMessageCount: number;
  scheduledMessageCount: number;
  transferDeadLetterMessageCount: number;
  sizeInBytes: number;
  accessedAt?: string;
  status?: string;
}

export interface ServiceBusDeadLetterMessage {
  sequenceNumber: number;
  messageId: string;
  deadLetterReason?: string;
  deadLetterErrorDescription?: string;
  deliveryCount: number;
  enqueuedTimeUtc?: string;
  deadLetterTimeUtc?: string;
}

export interface ServiceBusDeadLetterPagedResult {
  totalCount: number;
  page: number;
  pageSize: number;
  totalPages: number;
  messages: ServiceBusDeadLetterMessage[];
}

export interface ServiceBusDeadLetterMessageDetail extends ServiceBusDeadLetterMessage {
  correlationId?: string;
  subject?: string;
  contentType?: string;
  body?: string;
  applicationProperties?: Record<string, unknown>;
  deadLetterSource?: string;
  deadLetterTimeUtc?: string;
  expiresAt?: string;
  sessionId?: string;
  size?: number;
}

export interface ServiceBusDeadLetterOperationError {
  sequenceNumber: number;
  error: string;
}

export interface ServiceBusDeadLetterOperationResult {
  succeeded: number;
  failed: number;
  errors: ServiceBusDeadLetterOperationError[];
}

export interface TemplateVariableInfo {
  name: string;
  description: string;
  exampleValue: string;
}

export interface EmailTemplateDto {
  id: string;
  notificationType: number;
  notificationTypeName: string;
  name: string;
  subjectTemplate: string;
  bodyTemplate: string;
  availableVariables: TemplateVariableInfo[];
  isActive: boolean;
  createdAt: string;
  updatedAt?: string;
}

export interface PreviewEmailTemplateResult {
  renderedSubject: string;
  renderedBodyHtml: string;
}

export type BatchDeleteStreamStatus = 'started' | 'in_progress' | 'completed';

export interface BatchDeleteStreamUpdate {
  status: BatchDeleteStreamStatus;
  deleted: number;
  total: number;
}

export interface DeadLetterQueueMetrics {
  queueName: string;
  messageCount: number;
  recentMessages: unknown[];
}

class ApiClient {
  private readonly client: AxiosInstance;

  constructor() {
    this.client = axios.create({
      baseURL: API_BASE_URL,
      headers: { 'Content-Type': 'application/json' },
    });

    this.client.interceptors.request.use((config) => {
      const token = localStorage.getItem('auth_token');
      config.headers['X-Correlation-ID'] = getCurrentCorrelationId();
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
      return config;
    });
  }

  private async readNdjsonStream<T>(
    response: Response,
    onItem: (item: T) => void,
  ): Promise<void> {
    if (!response.body) {
      throw new Error('Streaming response body is not available.');
    }

    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    let buffer = '';

    while (true) {
      const { done, value } = await reader.read();
      if (done) {
        break;
      }

      buffer += decoder.decode(value, { stream: true });
      const lines = buffer.split('\n');
      buffer = lines.pop() ?? '';

      for (const line of lines) {
        const trimmed = line.trim();
        if (!trimmed) {
          continue;
        }
        onItem(JSON.parse(trimmed) as T);
      }
    }

    const remaining = buffer.trim();
    if (remaining) {
      onItem(JSON.parse(remaining) as T);
    }
  }

  private async getFetchErrorMessage(response: Response, fallback: string): Promise<string> {
    try {
      const contentType = response.headers.get('content-type') ?? '';
      if (contentType.includes('application/json')) {
        const errorPayload = await response.json() as {
          title?: string;
          detail?: string;
          message?: string;
          error?: string;
        };
        return errorPayload.detail
          ?? errorPayload.title
          ?? errorPayload.message
          ?? errorPayload.error
          ?? fallback;
      }

      const responseText = (await response.text()).trim();
      return responseText || fallback;
    } catch {
      return fallback;
    }
  }

  async login(data: LoginRequest): Promise<LoginResponse> {
    const response = await this.client.post<LoginResponse>('/api/auth/login', data);
    return response.data;
  }

  async register(data: RegisterRequest): Promise<RegisterResponse> {
    const response = await this.client.post<RegisterResponse>('/api/auth/register', data);
    return response.data;
  }

  async validateToken(): Promise<AuthUser> {
    const response = await this.client.get<AuthUser>('/api/auth/validate');
    return response.data;
  }

  async reportClientErrors(errors: unknown[]): Promise<void> {
    await this.client.post('/api/client-errors', { errors });
  }

  async getSystemSettings(): Promise<SystemSettingsTyped> {
    const response = await this.client.get<SystemSettingsTyped>('/api/system-settings');
    return {
      ...response.data,
      aiCredits: {
        globalMonthlyProviderCostCapUsd: response.data.aiCredits?.globalMonthlyProviderCostCapUsd ?? null,
        globalDailyProviderCostCapUsd: response.data.aiCredits?.globalDailyProviderCostCapUsd ?? null,
        emergencyStop: response.data.aiCredits?.emergencyStop ?? false,
        defaultPricePerCreditUsd: response.data.aiCredits?.defaultPricePerCreditUsd ?? 0.03,
        tierDefaults: {
          Trial: response.data.aiCredits?.tierDefaults?.Trial ?? 50,
          Starter: response.data.aiCredits?.tierDefaults?.Starter ?? 200,
          Professional: response.data.aiCredits?.tierDefaults?.Professional ?? 2000,
          Enterprise: response.data.aiCredits?.tierDefaults?.Enterprise ?? 20000,
        },
        tierMaximums: {
          Trial: response.data.aiCredits?.tierMaximums?.Trial ?? 50,
          Starter: response.data.aiCredits?.tierMaximums?.Starter ?? 200,
          Professional: response.data.aiCredits?.tierMaximums?.Professional ?? 2000,
          Enterprise: response.data.aiCredits?.tierMaximums?.Enterprise ?? 20000,
        },
        tierProviderCostCapsUsd: {
          Trial: response.data.aiCredits?.tierProviderCostCapsUsd?.Trial ?? 1,
          Starter: response.data.aiCredits?.tierProviderCostCapsUsd?.Starter ?? 5,
          Professional: response.data.aiCredits?.tierProviderCostCapsUsd?.Professional ?? 20,
          Enterprise: response.data.aiCredits?.tierProviderCostCapsUsd?.Enterprise ?? 50,
        },
      },
      incidentChatTierCeilings: {
        trial: response.data.incidentChatTierCeilings?.trial ?? 50,
        starter: response.data.incidentChatTierCeilings?.starter ?? 50,
        professional: response.data.incidentChatTierCeilings?.professional ?? 100,
        enterprise: response.data.incidentChatTierCeilings?.enterprise ?? 250,
      },
    };
  }

  async updateSystemSettings(settings: SystemSettingsTyped): Promise<SystemSettingsTyped> {
    const response = await this.client.put<SystemSettingsTyped>('/api/system-settings', settings);
    return response.data;
  }

  // Incident AI chat default policy (System Admin)
  async getIncidentAiChatPlatformPolicy(): Promise<IncidentAiChatPlatformPolicy> {
    const response = await this.client.get<IncidentAiChatPlatformPolicy>(
      '/api/system-settings/incident-ai-chat-policy',
    );
    return response.data;
  }

  async updateIncidentAiChatPlatformDefault(defaultLimit: number): Promise<IncidentAiChatPlatformPolicy> {
    const response = await this.client.put<IncidentAiChatPlatformPolicy>(
      '/api/system-settings/incident-ai-chat-policy/default',
      { defaultLimit },
    );
    return response.data;
  }

  async updateIncidentAiChatPlatformTenantOverride(
    tenantId: string,
    limit: number | null,
  ): Promise<IncidentAiChatPlatformTenantPolicy> {
    const response = await this.client.put<IncidentAiChatPlatformTenantPolicy>(
      `/api/system-settings/incident-ai-chat-policy/tenants/${tenantId}`,
      { limit },
    );
    return response.data;
  }

  async getSystemLogs(query: Query = {}): Promise<SystemLogsResponse> {
    const response = await this.client.get<SystemLogsResponse>(`/api/system/logs?${toQueryString(query)}`);
    return response.data;
  }

  async getScheduledJobs(): Promise<ScheduledJobDto[]> {
    const response = await this.client.get<ScheduledJobDto[]>('/api/system-monitoring/scheduled-jobs');
    return response.data;
  }

  async getServiceBusHealth(): Promise<ServiceBusHealthSummary> {
    try {
      const response = await this.client.get<ServiceBusHealthSummary>('/api/service-bus/health');
      return response.data;
    } catch {
      const response = await this.client.get<ServiceBusHealthSummary>('/api/system-monitoring/service-bus/health');
      return response.data;
    }
  }

  async getPerformanceSummary(hours = 24): Promise<PerformanceSummaryDto> {
    const response = await this.client.get<PerformanceSummaryDto>('/api/system/performance/summary', { params: { hours } });
    return response.data;
  }

  async getAutoCloseStatus(): Promise<unknown> {
    const response = await this.client.get('/api/system-monitoring/auto-close-status');
    return response.data;
  }

  async getSignInLogs(query: Query = {}): Promise<SignInLogsResponse> {
    const response = await this.client.get<SignInLogsResponse>(`/api/signin-logs?${toQueryString(query)}`);
    return response.data;
  }

  async getEmailHistory(query: Query = {}): Promise<unknown> {
    try {
      const response = await this.client.get(`/api/system/email-history?${toQueryString(query)}`);
      return response.data;
    } catch {
      const response = await this.client.get(`/api/system-monitoring/email-history?${toQueryString(query)}`);
      return response.data;
    }
  }

  async getFeedbackAndBugs(): Promise<unknown> {
    const response = await this.client.get('/api/system/feedback-bugs');
    return response.data;
  }

  async getSystemAssistantUsage(query: Query = {}): Promise<AssistantUsageSummary> {
    const response = await this.client.get<AssistantUsageSummary | AssistantUsageApiRow[]>('/api/system-monitoring/assistant-usage', {
      params: query,
    });

    if (Array.isArray(response.data)) {
      const rows = response.data.map((row) => {
        const promptTokens = Number(row.totalPromptTokens ?? 0);
        const completionTokens = Number(row.totalCompletionTokens ?? 0);
        const totalTokens = Number(row.totalTokens ?? 0);
        const interactions = Number(row.totalRequests ?? 0);
        const estimatedCost =
          (promptTokens * ASSISTANT_INPUT_COST_PER_MILLION) / 1_000_000 +
          (completionTokens * ASSISTANT_OUTPUT_COST_PER_MILLION) / 1_000_000;

        return {
          tenantId: row.tenantId,
          tenantName: row.tenantName ?? row.tenantId,
          promptTokens,
          completionTokens,
          totalTokens,
          interactions,
          estimatedCost,
        };
      });

      const totalPromptTokens = rows.reduce((sum, row) => sum + row.promptTokens, 0);
      const totalCompletionTokens = rows.reduce((sum, row) => sum + row.completionTokens, 0);
      const totalTokens = rows.reduce((sum, row) => sum + row.totalTokens, 0);
      const totalInteractions = rows.reduce((sum, row) => sum + row.interactions, 0);
      const totalEstimatedCost =
        (totalPromptTokens * ASSISTANT_INPUT_COST_PER_MILLION) / 1_000_000 +
        (totalCompletionTokens * ASSISTANT_OUTPUT_COST_PER_MILLION) / 1_000_000;

      return {
        totalPromptTokens,
        totalCompletionTokens,
        totalTokens,
        totalInteractions,
        totalEstimatedCost,
        rows,
      };
    }

    if (response.data && Array.isArray(response.data.rows)) {
      return response.data;
    }

    throw new Error('Unexpected assistant usage response format from /api/system-monitoring/assistant-usage');
  }

  async getSystemInfo(): Promise<SystemInfoResponse> {
    const response = await this.client.get<SystemInfoResponse>('/api/system/info');
    return response.data;
  }

  async getItilConfig(): Promise<unknown> {
    const response = await this.client.get('/api/system/itil-configuration');
    return response.data;
  }

  async getSystemPriorityTemplates(): Promise<SystemPriorityTemplateDto[]> {
    const response = await this.client.get<SystemPriorityTemplateDto[]>('/api/system/priority-templates');
    return response.data;
  }

  async upsertSystemPriorityTemplate(
    data: UpsertSystemPriorityTemplateRequest,
    id?: string,
  ): Promise<SystemPriorityTemplateDto> {
    if (id) {
      const response = await this.client.put<SystemPriorityTemplateDto>(`/api/system/priority-templates/${id}`, data);
      return response.data;
    }
    const response = await this.client.post<SystemPriorityTemplateDto>('/api/system/priority-templates', data);
    return response.data;
  }

  async deleteSystemPriorityTemplate(id: string): Promise<void> {
    await this.client.delete(`/api/system/priority-templates/${id}`);
  }

  async getSystemReasonTemplates(): Promise<SystemReasonTemplateDto[]> {
    const response = await this.client.get<SystemReasonTemplateDto[]>('/api/system/reason-templates');
    return response.data;
  }

  async upsertSystemReasonTemplate(data: UpsertSystemReasonTemplateRequest, id?: string): Promise<SystemReasonTemplateDto> {
    if (id) {
      const response = await this.client.put<SystemReasonTemplateDto>(`/api/system/reason-templates/${id}`, data);
      return response.data;
    }
    const response = await this.client.post<SystemReasonTemplateDto>('/api/system/reason-templates', data);
    return response.data;
  }

  async deleteSystemReasonTemplate(id: string): Promise<void> {
    await this.client.delete(`/api/system/reason-templates/${id}`);
  }

  async getTenantPriorityConfig(tenantId: string, customerId?: string): Promise<TenantPriorityConfigDto> {
    const response = await this.client.get<TenantPriorityConfigDto>(
      `/api/tenants/${tenantId}/priority-configuration`,
      { params: customerId ? { customerId } : undefined },
    );
    return response.data;
  }

  async getTenantReasonConfig(tenantId: string, customerId?: string): Promise<TenantReasonConfigDto> {
    const response = await this.client.get<TenantReasonConfigDto>(
      `/api/tenants/${tenantId}/reason-config`,
      { params: customerId ? { customerId } : undefined },
    );
    return response.data;
  }

  async getTeams(tenantId: string, includeInactive = false): Promise<Team[]> {
    const response = await this.client.get<Team[]>(`/api/tenants/${tenantId}/teams`, {
      params: includeInactive ? { includeInactive } : undefined,
    });
    return response.data;
  }

  async getCiClasses(tenantId: string, includeInactive = false): Promise<CiClassDto[]> {
    const response = await this.client.get<CiClassDto[]>(`/api/tenants/${tenantId}/ci-classes`, {
      params: includeInactive ? { includeInactive } : undefined,
    });
    return response.data;
  }

  async getCorrelationRules(
    tenantId: string,
    params?: { isEnabled?: boolean; correlationType?: string },
  ): Promise<CorrelationRuleDto[]> {
    const response = await this.client.get<CorrelationRuleDto[]>(`/api/tenants/${tenantId}/correlation-rules`, { params });
    return response.data;
  }

  async getCorrelationRule(tenantId: string, id: string): Promise<CorrelationRuleDto> {
    const response = await this.client.get<CorrelationRuleDto>(`/api/tenants/${tenantId}/correlation-rules/${id}`);
    return response.data;
  }

  async getScripts(tenantId: string, customerId?: string): Promise<ScriptSummaryDto[]> {
    const response = await this.client.get<ScriptSummaryDto[]>(`/api/tenants/${tenantId}/scripts`, {
      params: customerId ? { customerId } : undefined,
    });
    return response.data;
  }

  async getServiceConnections(tenantId: string, customerId: string): Promise<ServiceConnectionListDto> {
    const response = await this.client.get<ServiceConnectionListDto>(
      `/api/tenants/${tenantId}/customers/${customerId}/service-connections`,
    );
    return response.data;
  }

  async getSystemSlaPolicyTemplates(): Promise<SystemSlaPolicyTemplateDto[]> {
    const response = await this.client.get<SystemSlaPolicyTemplateDto[]>('/api/system/sla-templates');
    return response.data;
  }

  async createSystemSlaPolicyTemplate(data: CreateSystemSlaPolicyTemplateDto): Promise<SystemSlaPolicyTemplateDto> {
    const response = await this.client.post<SystemSlaPolicyTemplateDto>('/api/system/sla-templates', data);
    return response.data;
  }

  async updateSystemSlaPolicyTemplate(
    id: string,
    data: UpdateSystemSlaPolicyTemplateDto,
  ): Promise<SystemSlaPolicyTemplateDto> {
    const response = await this.client.put<SystemSlaPolicyTemplateDto>(`/api/system/sla-templates/${id}`, data);
    return response.data;
  }

  async deleteSystemSlaPolicyTemplate(id: string): Promise<void> {
    await this.client.delete(`/api/system/sla-templates/${id}`);
  }

  async getVersionHistory(): Promise<VersionHistoryResponse> {
    const response = await this.client.get<VersionHistoryResponse>('/api/system/version-history');
    return response.data;
  }

  async getPipelineDefaults(): Promise<PipelineDefaultsDto[]> {
    const response = await this.client.get<PipelineDefaultsDto[]>('/api/system/pipeline-defaults');
    return response.data;
  }

  async updatePipelineDefaults(id: string, data: Partial<PipelineDefaultsDto>): Promise<PipelineDefaultsDto> {
    const response = await this.client.put<PipelineDefaultsDto>(`/api/system/pipeline-defaults/${id}`, data);
    return response.data;
  }

  async getAllPipelineDefaults(): Promise<PipelineDefaultsListDto> {
    try {
      const response = await this.client.get<PipelineDefaultsListDto>('/api/system-settings/pipeline-defaults');
      return response.data;
    } catch {
      const legacyDefaults = await this.getPipelineDefaults();
      const endpointTypes: PipelineDefaultsSummaryDto[] = legacyDefaults.map((item) => ({
        endpointType: item.endpointType,
        hasCustomDefaults: Boolean(item.id),
        customStepCount: item.defaultSteps?.length ?? 0,
        lastModified: item.updatedAt,
      }));
      return { endpointTypes };
    }
  }

  async getPipelineDefaultsByEndpoint(endpointType: string): Promise<PipelineDefaultsDto> {
    try {
      const response = await this.client.get<PipelineDefaultsDto>(`/api/system-settings/pipeline-defaults/${endpointType}`);
      return response.data;
    } catch {
      const legacyDefaults = await this.getPipelineDefaults();
      const endpointDefaults = legacyDefaults.find((item) => item.endpointType === endpointType);
      if (!endpointDefaults) {
        throw new Error(`No pipeline defaults found for endpoint type ${endpointType}`);
      }
      return endpointDefaults;
    }
  }

  async updatePipelineDefaultsByEndpoint(
    endpointType: string,
    data: Partial<PipelineDefaultsDto>,
  ): Promise<PipelineDefaultsDto> {
    try {
      const response = await this.client.put<PipelineDefaultsDto>(`/api/system-settings/pipeline-defaults/${endpointType}`, data);
      return response.data;
    } catch {
      const legacyDefaults = await this.getPipelineDefaults();
      const endpointDefaults = legacyDefaults.find((item) => item.endpointType === endpointType);
      if (!endpointDefaults?.id) {
        throw new Error(`Unable to update defaults for endpoint type ${endpointType}`);
      }
      const response = await this.client.put<PipelineDefaultsDto>(
        `/api/system/pipeline-defaults/${endpointDefaults.id}`,
        { configuration: data },
      );
      return response.data;
    }
  }

  async deletePipelineDefaults(endpointType: string): Promise<void> {
    try {
      await this.client.delete(`/api/system-settings/pipeline-defaults/${endpointType}`);
      return;
    } catch {
      const legacyDefaults = await this.getPipelineDefaults();
      const endpointDefaults = legacyDefaults.find((item) => item.endpointType === endpointType);
      if (!endpointDefaults?.id) {
        throw new Error(`Unable to reset defaults for endpoint type ${endpointType}`);
      }
      await this.client.delete(`/api/system/pipeline-defaults/${endpointDefaults.id}`);
    }
  }

  async previewTruncateEvents(params: {
    tenantId?: string;
    statusFilter?: number;
    olderThan?: string;
  }): Promise<{ count: number }> {
    const response = await this.client.post<{ count: number }>(
      '/api/system/events/truncate/preview',
      params,
    );
    return response.data;
  }

  async truncateEventsStreaming(
    params: {
      tenantId?: string;
      statusFilter?: number;
      olderThan?: string;
    },
    onProgress: (progress: BatchDeleteStreamUpdate) => void,
  ): Promise<void> {
    const token = localStorage.getItem('auth_token');
    const response = await fetch(`${API_BASE_URL}/api/system/events/truncate/stream`, {
      method: 'POST',
      headers: {
        Authorization: token ? `Bearer ${token}` : '',
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(params),
    });

    if (!response.ok) {
      const fallbackMessage = `Truncation failed: ${response.status} ${response.statusText}`;
      throw new Error(await this.getFetchErrorMessage(response, fallbackMessage));
    }

    await this.readNdjsonStream<BatchDeleteStreamUpdate>(response, onProgress);
  }

  async getEmailTemplates(): Promise<EmailTemplateDto[]> {
    const response = await this.client.get<EmailTemplateDto[]>('/api/system/email-templates');
    return response.data;
  }

  async updateEmailTemplate(
    id: string,
    data: {
      subjectTemplate: string;
      bodyTemplate: string;
      isActive: boolean;
    },
  ): Promise<EmailTemplateDto> {
    const response = await this.client.put<EmailTemplateDto>(`/api/system/email-templates/${id}`, data);
    return response.data;
  }

  async resetEmailTemplate(id: string): Promise<EmailTemplateDto> {
    const response = await this.client.post<EmailTemplateDto>(`/api/system/email-templates/${id}/reset`);
    return response.data;
  }

  async previewEmailTemplate(data: {
    subjectTemplate: string;
    bodyTemplate: string;
    notificationType: number;
  }): Promise<PreviewEmailTemplateResult> {
    const response = await this.client.post<PreviewEmailTemplateResult>(
      '/api/system/email-templates/preview',
      data,
    );
    return response.data;
  }

  async testSendGridConnection(): Promise<{ success: boolean; message: string }> {
    const response = await this.client.post<{ success: boolean; message: string }>('/api/system/settings/test-sendgrid');
    return response.data;
  }

  async sendTestEmail(request: { toEmail: string }): Promise<{ success: boolean; message: string }> {
    const response = await this.client.post<{ success: boolean; message: string }>('/api/system-settings/test-email', request);
    return response.data;
  }

  async getDeadLetterMetrics(queueName: string): Promise<DeadLetterQueueMetrics> {
    try {
      const response = await this.client.get<DeadLetterQueueMetrics>(
        `/api/service-bus/queues/${queueName}/dead-letter`,
      );
      return response.data;
    } catch {
      const response = await this.client.get<DeadLetterQueueMetrics>(
        `/api/system-monitoring/service-bus/queues/${queueName}/dead-letter`,
      );
      return response.data;
    }
  }

  async purgeDeadLetterQueue(queueName: string): Promise<void> {
    try {
      await this.client.post(`/api/service-bus/queues/${queueName}/dead-letter/purge`);
    } catch {
      await this.client.post(`/api/system-monitoring/service-bus/queues/${queueName}/dead-letter/purge`);
    }
  }

  async resubmitAllDeadLetterMessages(queueName: string): Promise<void> {
    try {
      await this.client.post(`/api/service-bus/queues/${queueName}/dead-letter/resubmit-all`);
    } catch {
      await this.client.post(`/api/system-monitoring/service-bus/queues/${queueName}/dead-letter/resubmit-all`);
    }
  }

  async getSystemTenants(): Promise<TenantSummary[]> {
    const response = await this.client.get<TenantSummaryApiResponse[]>('/api/tenants/admin/all');
    return response.data.map(mapTenantSummary);
  }

  async retryJob(jobId: string): Promise<void> {
    await this.client.post(`/api/system-monitoring/scheduled-jobs/${jobId}/retry`);
  }

  async cancelJob(jobId: string): Promise<void> {
    await this.client.post(`/api/system-monitoring/scheduled-jobs/${jobId}/cancel`);
  }

  async getDocumentation(): Promise<unknown> {
    const response = await this.client.get('/api/system/documentation');
    return response.data;
  }

  async getTenants(): Promise<TenantSummary[]> {
    try {
      const response = await this.client.get<TenantSummaryApiResponse[]>('/api/tenants');
      return response.data.map(mapTenantSummary);
    } catch {
      const response = await this.client.get<TenantSummaryApiResponse[]>('/api/tenants/admin/all');
      return response.data.map(mapTenantSummary);
    }
  }

  async getBuiltInUsers(): Promise<BuiltInUser[]> {
    try {
      const response = await this.client.get<BuiltInUser[]>('/api/system/built-in-users');
      return response.data;
    } catch {
      const response = await this.client.get<BuiltInUser[]>('/api/users', { params: { isSystemUser: true } });
      return response.data;
    }
  }

  async getEmailLogs(tenantId: string, query: Query = {}): Promise<EmailLogDto[]> {
    const response = await this.client.get<EmailLogDto[]>(`/api/tenants/${tenantId}/email-logs?${toQueryString(query)}`);
    return response.data;
  }

  async getSignInLogsDetailed(query: Query = {}): Promise<SignInLogsDetailedResponse> {
    const raw = await this.getSignInLogs(query) as SignInLogsResponse & {
      logs?: SignInLogsDetailedResponse['logs'];
      totalPages?: number;
    };

    if (Array.isArray(raw.logs)) {
      return {
        logs: raw.logs,
        totalCount: raw.totalCount ?? 0,
        page: raw.page ?? 1,
        pageSize: raw.pageSize ?? 25,
        totalPages: raw.totalPages ?? Math.max(1, Math.ceil((raw.totalCount ?? 0) / Math.max(1, raw.pageSize ?? 25))),
      };
    }

    return {
      logs: (raw.items ?? []) as SignInLogsDetailedResponse['logs'],
      totalCount: raw.totalCount ?? 0,
      page: raw.page ?? 1,
      pageSize: raw.pageSize ?? 25,
      totalPages: Math.max(1, Math.ceil((raw.totalCount ?? 0) / Math.max(1, raw.pageSize ?? 25))),
    };
  }

  async getBugReports(
    tenantId: string,
    query: {
      page?: number;
      pageSize?: number;
      sortBy?: string;
      sortDirection?: 'asc' | 'desc';
      isAutoGenerated?: boolean;
    } = {},
  ): Promise<PagedResult<BugReportDto>> {
    const response = await this.client.get<PagedResult<BugReportDto>>(`/api/tenants/${tenantId}/bug-reports?${toQueryString(query)}`);
    return response.data;
  }

  async updateBugReportStatus(tenantId: string, bugReportId: string, status: string): Promise<void> {
    await this.client.patch(`/api/tenants/${tenantId}/bug-reports/${bugReportId}/status`, { status });
  }

  async getFeedback(tenantId: string): Promise<FeedbackDto[]> {
    const response = await this.client.get<FeedbackDto[]>(`/api/tenants/${tenantId}/feedback`);
    return response.data;
  }

  async createFeedback(tenantId: string, request: CreateFeedbackRequest): Promise<string> {
    const response = await this.client.post<string>(`/api/tenants/${tenantId}/feedback`, request);
    return response.data;
  }

  async updateFeedbackStatus(tenantId: string, feedbackId: string, status: string): Promise<void> {
    await this.client.patch(`/api/tenants/${tenantId}/feedback/${feedbackId}/status`, { status });
  }

  async getNavLinkById(tenantId: string, navLinkId: string): Promise<SavedNavLinkDto> {
    const response = await this.client.get<SavedNavLinkDto>(`/api/tenants/${tenantId}/nav-links/${navLinkId}`);
    return response.data;
  }

  async createSavedNavLink(tenantId: string, request: CreateSavedNavLinkRequest): Promise<SavedNavLinkDto> {
    const response = await this.client.post<SavedNavLinkDto>(`/api/tenants/${tenantId}/nav-links`, request);
    return response.data;
  }

  async getGroupedSlowQueries(
    hours = 24,
    limit = 50,
  ): Promise<Array<{
    endpointOrQuery: string;
    tableName?: string | null;
    operation?: string | null;
    executionCount: number;
    minDurationMs: number;
    avgDurationMs: number;
    maxDurationMs: number;
    totalDurationMs: number;
    lastSeen: string;
  }>> {
    const response = await this.client.get('/api/system/performance/slow-queries/grouped', {
      params: { hours, limit },
    });
    return response.data;
  }

  async getPerformanceByEndpoint(hours = 24): Promise<Array<{
    requestPath: string;
    queryCount: number;
    avgDurationMs: number;
    p95DurationMs: number;
    maxDurationMs: number;
    slowQueryCount: number;
    method?: string;
    errorRate?: number;
    requestCount?: number;
  }>> {
    const response = await this.client.get('/api/system/performance/by-endpoint', {
      params: { hours },
    });
    return response.data;
  }

  async getPerformanceByTable(hours = 24): Promise<Array<{
    tableOrContainer: string;
    queryCount: number;
    avgDurationMs?: number;
    p95DurationMs?: number;
    maxDurationMs?: number;
    slowQueryCount?: number;
  }>> {
    const response = await this.client.get('/api/system/performance/by-table', {
      params: { hours },
    });
    return response.data;
  }

  async getPerformanceTrends(
    hours = 24,
    interval = 'hourly',
  ): Promise<Array<{
    bucketStart: string;
    queryCount: number;
    avgDurationMs: number;
    p95DurationMs: number;
    slowQueryCount: number;
  }>> {
    const response = await this.client.get('/api/system/performance/trends', {
      params: { hours, interval },
    });
    return response.data;
  }

  async getTenantIncidents(
    tenantId: string,
    query: Query = {},
  ): Promise<{
    incidents: Array<{
      id: string;
      number?: string;
      title?: string;
      createdAt: string;
      resolvedAt?: string;
      closedAt?: string;
      status?: string;
    }>;
    totalCount: number;
    page: number;
    pageSize: number;
    totalPages: number;
  }> {
    const response = await this.client.get(`/api/tenants/${tenantId}/incidents?${toQueryString(query)}`);
    return response.data;
  }

  async getServiceBusQueues(): Promise<ServiceBusQueueMetrics[]> {
    try {
      const response = await this.client.get<ServiceBusQueueMetrics[]>('/api/service-bus/queues');
      return response.data;
    } catch {
      const response = await this.client.get<ServiceBusQueueMetrics[]>('/api/system-monitoring/service-bus/queues');
      return response.data;
    }
  }

  async getServiceBusDeadLetterMessages(
    queueName: string,
    page: number = 1,
    pageSize: number = 25,
  ): Promise<ServiceBusDeadLetterPagedResult> {
    try {
      const response = await this.client.get<ServiceBusDeadLetterPagedResult>(
        `/api/service-bus/queues/${queueName}/dead-letter/messages`,
        { params: { page, pageSize } },
      );
      return response.data;
    } catch {
      const response = await this.client.get<ServiceBusDeadLetterPagedResult>(
        `/api/system-monitoring/service-bus/queues/${queueName}/dead-letter/messages`,
        { params: { page, pageSize } },
      );
      return response.data;
    }
  }

  async getServiceBusDeadLetterMessageDetail(
    queueName: string,
    sequenceNumber: number,
  ): Promise<ServiceBusDeadLetterMessageDetail> {
    try {
      const response = await this.client.get<ServiceBusDeadLetterMessageDetail>(
        `/api/service-bus/queues/${queueName}/dead-letter/messages/${sequenceNumber}`,
      );
      return response.data;
    } catch {
      const response = await this.client.get<ServiceBusDeadLetterMessageDetail>(
        `/api/system-monitoring/service-bus/queues/${queueName}/dead-letter/messages/${sequenceNumber}`,
      );
      return response.data;
    }
  }

  async resubmitServiceBusDeadLetterMessages(
    queueName: string,
    sequenceNumbers: number[],
  ): Promise<void> {
    try {
      await this.client.post(`/api/service-bus/queues/${queueName}/dead-letter/resubmit`, {
        sequenceNumbers,
      });
    } catch {
      await this.client.post(`/api/system-monitoring/service-bus/queues/${queueName}/dead-letter/resubmit`, {
        sequenceNumbers,
      });
    }
  }

  async purgeServiceBusDeadLetterQueue(queueName: string): Promise<void> {
    try {
      await this.client.post(`/api/service-bus/queues/${queueName}/dead-letter/purge`);
    } catch {
      await this.client.post(`/api/system-monitoring/service-bus/queues/${queueName}/dead-letter/purge`);
    }
  }

  async resubmitAllServiceBusDeadLetterMessages(queueName: string): Promise<void> {
    try {
      await this.client.post(`/api/service-bus/queues/${queueName}/dead-letter/resubmit-all`);
    } catch {
      await this.client.post(`/api/system-monitoring/service-bus/queues/${queueName}/dead-letter/resubmit-all`);
    }
  }

  async resubmitServiceBusDeadLetterMessageWithEdit(
    queueName: string,
    sequenceNumber: number,
    modifiedBody: string,
  ): Promise<void> {
    try {
      await this.client.post(`/api/service-bus/queues/${queueName}/dead-letter/resubmit-with-edit`, {
        sequenceNumber,
        modifiedBody,
      });
    } catch {
      await this.client.post(`/api/system-monitoring/service-bus/queues/${queueName}/dead-letter/resubmit-with-edit`, {
        sequenceNumber,
        modifiedBody,
      });
    }
  }

  async deleteServiceBusDeadLetterMessages(
    queueName: string,
    sequenceNumbers: number[],
  ): Promise<ServiceBusDeadLetterOperationResult> {
    try {
      const response = await this.client.post<ServiceBusDeadLetterOperationResult>(
        `/api/service-bus/queues/${queueName}/dead-letter/delete`,
        { sequenceNumbers },
      );
      return response.data;
    } catch {
      const response = await this.client.post<ServiceBusDeadLetterOperationResult>(
        `/api/system-monitoring/service-bus/queues/${queueName}/dead-letter/delete`,
        { sequenceNumbers },
      );
      return response.data;
    }
  }
  // ─────────────────────────────────────────────────────────────────────────
  // Platform Integrations (System Admin)
  // ─────────────────────────────────────────────────────────────────────────

  async getPlatformTeamsApp(): Promise<PlatformTeamsAppSettingsDto> {
    const response = await this.client.get<PlatformTeamsAppSettingsDto>('/api/system/integrations/teams');
    return response.data;
  }

  async upsertPlatformTeamsApp(request: UpsertPlatformTeamsAppRequest): Promise<PlatformTeamsAppSettingsDto> {
    const response = await this.client.put<PlatformTeamsAppSettingsDto>('/api/system/integrations/teams', request);
    return response.data;
  }

  async setPlatformTeamsAppSecret(request: SetPlatformTeamsAppSecretRequest): Promise<PlatformTeamsAppSettingsDto> {
    const response = await this.client.put<PlatformTeamsAppSettingsDto>('/api/system/integrations/teams/secret', request);
    return response.data;
  }

  async clearPlatformTeamsAppSecret(): Promise<PlatformTeamsAppSettingsDto> {
    const response = await this.client.delete<PlatformTeamsAppSettingsDto>('/api/system/integrations/teams/secret');
    return response.data;
  }

  async testPlatformTeamsAppCredentials(): Promise<TestPlatformTeamsAppCredentialsResult> {
    const response = await this.client.post<TestPlatformTeamsAppCredentialsResult>('/api/system/integrations/teams/test');
    return response.data;
  }

  async getPlatformTeamsSetupInfo(): Promise<TeamsSetupInfoDto> {
    const response = await this.client.get<TeamsSetupInfoDto>('/api/system/integrations/teams/setup-info');
    return response.data;
  }

  /**
   * Returns the absolute URL the browser can navigate to in order to download
   * the operator setup PowerShell script. The script itself is served by the
   * backend with placeholders for the current environment already substituted.
   */
  getPlatformTeamsSetupScriptUrl(): string {
    const base = this.client.defaults.baseURL?.replace(/\/$/, '') ?? '';
    return `${base}/api/system/integrations/teams/setup-script`;
  }

  // AI Credits (System Admin)
  async getSystemAiCreditsOverview(days: number = 30): Promise<SystemAiCreditsOverview> {
    const response = await this.client.get<Record<string, unknown>>('/api/system-monitoring/ai-credits', {
      params: { days },
    });

    const rawTenantItems = (response.data.tenantBreakdown ?? response.data.tenantSummaries) as Array<Record<string, unknown>> | undefined;

    const normalizedTenantBreakdown: TenantAiCreditSummary[] = (rawTenantItems ?? []).map((tenant) => ({
      tenantId: String(tenant.tenantId ?? ''),
      tenantName: String(tenant.tenantName ?? tenant.tenantId ?? 'Unknown Tenant'),
      tier: tenant.tier === undefined ? undefined : String(tenant.tier),
      isActive: tenant.isActive === undefined ? undefined : Boolean(tenant.isActive),
      creditsUsed: Number(tenant.creditsUsed ?? tenant.usedCredits ?? 0),
      creditsRemaining: Number(tenant.creditsRemaining ?? tenant.remainingCredits ?? 0),
      entitlementMonthlyCredits: tenant.entitlementMonthlyCredits === undefined ? undefined : Number(tenant.entitlementMonthlyCredits),
      effectiveMonthlyCredits: tenant.effectiveMonthlyCredits === undefined ? undefined : Number(tenant.effectiveMonthlyCredits),
      entitlementSource: tenant.entitlementSource === undefined ? undefined : String(tenant.entitlementSource),
      monthlyCreditsEntitlementOverride: tenant.monthlyCreditsEntitlementOverride === null || tenant.monthlyCreditsEntitlementOverride === undefined
        ? null
        : Number(tenant.monthlyCreditsEntitlementOverride),
      monthlyCreditsOperatingLimit: tenant.monthlyCreditsOperatingLimit === null || tenant.monthlyCreditsOperatingLimit === undefined
        ? null
        : Number(tenant.monthlyCreditsOperatingLimit),
      monthlyProviderCostCapUsdOverride: tenant.monthlyProviderCostCapUsdOverride === null || tenant.monthlyProviderCostCapUsdOverride === undefined
        ? null
        : Number(tenant.monthlyProviderCostCapUsdOverride),
      monthlyProviderCostCapOperatingLimitUsd: tenant.monthlyProviderCostCapOperatingLimitUsd === null || tenant.monthlyProviderCostCapOperatingLimitUsd === undefined
        ? null
        : Number(tenant.monthlyProviderCostCapOperatingLimitUsd),
      quotaUpdatedAtUtc: tenant.quotaUpdatedAtUtc === null || tenant.quotaUpdatedAtUtc === undefined
        ? null
        : String(tenant.quotaUpdatedAtUtc),
      monthlyCostCapUsd: tenant.monthlyCostCapUsd === null || tenant.monthlyCostCapUsd === undefined
        ? null
        : Number(tenant.monthlyCostCapUsd),
      monthlyProviderCostCapUsd: tenant.monthlyProviderCostCapUsd === null || tenant.monthlyProviderCostCapUsd === undefined
        ? null
        : Number(tenant.monthlyProviderCostCapUsd),
      totalCustomerCost: Number(tenant.totalCustomerCost ?? tenant.estimatedCost ?? 0),
      totalActualCost: Number(tenant.totalActualCost ?? 0),
      remainingCostBudgetUsd: tenant.remainingCostBudgetUsd === null || tenant.remainingCostBudgetUsd === undefined
        ? null
        : Number(tenant.remainingCostBudgetUsd),
      remainingProviderCostBudgetUsd: tenant.remainingProviderCostBudgetUsd === null || tenant.remainingProviderCostBudgetUsd === undefined
        ? null
        : Number(tenant.remainingProviderCostBudgetUsd),
      pricePerCredit: tenant.pricePerCredit === undefined ? undefined : Number(tenant.pricePerCredit),
      totalTokens: Number(tenant.totalTokens ?? tenant.totalTokensUsed ?? 0),
      estimatedCost: Number(tenant.estimatedCost ?? 0),
    }));

    return {
      totalCreditsUsedAllTenants: Number(response.data.totalCreditsUsedAllTenants ?? 0),
      totalTokensAllTenants: Number(response.data.totalTokensAllTenants ?? response.data.totalTokensUsedAllTenants ?? 0),
      totalEstimatedCost: Number(response.data.totalEstimatedCost ?? 0),
      totalActualCost: Number(response.data.totalActualCost ?? 0),
      platformPolicy: response.data.platformPolicy
        ? {
            globalMonthlyCostCapUsd: Number((response.data.platformPolicy as Record<string, unknown>).globalMonthlyCostCapUsd ?? 0),
            globalDailyCostCapUsd: Number((response.data.platformPolicy as Record<string, unknown>).globalDailyCostCapUsd ?? 0),
            emergencyStop: Boolean((response.data.platformPolicy as Record<string, unknown>).emergencyStop ?? false),
          }
        : undefined,
      modelRates: Array.isArray(response.data.modelRates)
        ? (response.data.modelRates as Array<Record<string, unknown>>).map((rate) => ({
            modelName: String(rate.modelName ?? ''),
            displayName: rate.displayName === undefined ? undefined : String(rate.displayName),
            baseTokensPerCredit: Number(rate.baseTokensPerCredit ?? 0),
            multiplier: Number(rate.multiplier ?? 1),
          }))
        : undefined,
      tenantBreakdown: normalizedTenantBreakdown,
    };
  }

  async getAiBudgetStatus(): Promise<AiBudgetStatusDto> {
    const response = await this.client.get<Record<string, unknown>>('/api/system-monitoring/ai-budget-status');
    const rawPeriods = Array.isArray(response.data.periods) ? response.data.periods as Array<Record<string, unknown>> : [];

    return {
      periods: rawPeriods.map((period) => ({
        startUtc: String(period.startUtc ?? ''),
        isDaily: Boolean(period.isDaily ?? false),
        usedUsd: Number(period.usedUsd ?? 0),
        reservedUsd: Number(period.reservedUsd ?? 0),
        accountingBlocked: Boolean(period.accountingBlocked ?? false),
      })),
      unsettledAttempts: Number(response.data.unsettledAttempts ?? 0),
      expiredUnsettledAttempts: Number(response.data.expiredUnsettledAttempts ?? 0),
      internalProviderCostUsd: Number(response.data.internalProviderCostUsd ?? 0),
    };
  }

  async updateTenantAiCreditSettings(
    tenantId: string,
    settings: UpdateTenantAiCreditSettingsRequest,
  ): Promise<void> {
    await this.client.put(`/api/tenants/${tenantId}/ai/settings`, settings);
  }

  async listTenantLifecycleTenants(): Promise<TenantLifecycleListItem[]> {
    const response = await this.client.get<TenantLifecycleListItem[]>('/api/system/tenant-lifecycle/tenants');
    return response.data;
  }

  async previewTenantDeletion(tenantId: string): Promise<TenantDeletionPreviewResult> {
    const response = await this.client.post<TenantDeletionPreviewResult>('/api/system/tenant-lifecycle/preview', {
      tenantId,
    });
    return response.data;
  }

  async updateTenantSubscription(command: UpdateTenantSubscriptionCommand): Promise<TenantSubscriptionResult> {
    const response = await this.client.put<TenantSubscriptionResult>('/api/system/tenant-lifecycle/subscription', command);
    return response.data;
  }

  async setTenantDeletionProtection(command: SetTenantDeletionProtectionCommand): Promise<TenantDeletionProtectionResult> {
    const response = await this.client.put<TenantDeletionProtectionResult>('/api/system/tenant-lifecycle/deletion-protection', command);
    return response.data;
  }

  async setTenantPurgeApproval(command: SetTenantPurgeApprovalCommand): Promise<TenantPurgeApprovalResult> {
    const response = await this.client.put<TenantPurgeApprovalResult>('/api/system/tenant-lifecycle/purge-approval', command);
    return response.data;
  }

  async scheduleTenantDeletion(command: ScheduleTenantDeletionCommand): Promise<ScheduleTenantDeletionResult> {
    const response = await this.client.post<ScheduleTenantDeletionResult>('/api/system/tenant-lifecycle/schedule', command);
    return response.data;
  }

  async restoreTenantDeletion(command: RestoreTenantDeletionCommand): Promise<RestoreTenantResult> {
    const response = await this.client.post<RestoreTenantResult>('/api/system/tenant-lifecycle/restore', command);
    return response.data;
  }

  async getTenantLifecycleStatus(tenantId: string): Promise<TenantLifecycleStatusResult> {
    const response = await this.client.get<TenantLifecycleStatusResult>(`/api/system/tenant-lifecycle/status/${tenantId}`);
    return response.data;
  }
}

export const apiClient = new ApiClient();
